import React, { useEffect, useState } from 'react';
import axiosInstance from '../services/axiosInstance';
import { PlusIcon, TrashIcon, EditIcon, LockIcon, XIcon, CheckIcon, BoltIcon, DropletIcon, FlameIcon, WifiIcon, SmartphoneIcon, PhoneIcon, HomeIcon, ThermometerIcon, FolderPlusIcon, TagIcon, LayoutGridIcon, ListIcon } from 'lucide-react';
import { toast } from 'sonner';

// Solo plan-only "Faturalar" (Bills) tab. Shows 8 system-default utility
// categories (Elektrik / Su / Doğalgaz / Internet / Mobil Telefon / Sabit
// Telefon / Aidat / Isıtma-Soğutma). Users can add custom labels under each
// (e.g. "Turkcell" under "Mobil Telefon"). System rows cannot be deleted;
// custom rows can be edited + deleted.
const PARENT_ICONS = {
  electric: BoltIcon,
  water: DropletIcon,
  gas: FlameIcon,
  internet: WifiIcon,
  mobile_phone: SmartphoneIcon,
  fixed_phone: PhoneIcon,
  building_fee: HomeIcon,
  hvac: ThermometerIcon,
};

const BillTypesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingFor, setAddingFor] = useState(null); // parent_key currently in add mode
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [creatingParent, setCreatingParent] = useState(false);
  const [newParentLabel, setNewParentLabel] = useState('');
  // Caps come from backend so they stay in sync with server-side enforcement
  const [limits, setLimits] = useState({ max_user_parents: 20, max_labels_per_parent: 50 });
  // Kart / Liste görünüm modu — kullanıcı tercihi localStorage'da tutulur
  // ('cc_bill_types_view'), diğer Masraf Merkezleri sekmelerindeki toggle
  // ile birebir uyumlu davranış.
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('cc_bill_types_view') || 'grid';
  });
  useEffect(() => {
    try {
      localStorage.setItem('cc_bill_types_view', viewMode);
    } catch (_) { /* ignore quota errors */ }
  }, [viewMode]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/bill-types');
      setItems(r.data.bill_types || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fatura tipleri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // Fetch caps in parallel — defaults already in state, this just updates if backend differs
    axiosInstance.get('/bill-types/limits')
      .then((r) => setLimits(r.data))
      .catch(() => { /* keep defaults */ });
  }, []);

  const groups = items.reduce((acc, it) => {
    if (!acc[it.parent_key]) acc[it.parent_key] = { system: null, customs: [] };
    // "Parent" row = custom_label is null (covers both system + user-created parents)
    if (it.custom_label === null) acc[it.parent_key].system = it;
    else acc[it.parent_key].customs.push(it);
    return acc;
  }, {});

  const handleCreateParent = async () => {
    const label = newParentLabel.trim();
    if (!label) return toast.error('Kategori adı boş olamaz');
    try {
      await axiosInstance.post('/bill-types/parents', { parent_label: label });
      toast.success('Kategori eklendi');
      setCreatingParent(false);
      setNewParentLabel('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kategori eklenemedi');
    }
  };

  const handleDeleteParent = async (parentId, label, childCount) => {
    const warn = childCount > 0
      ? `"${label}" kategorisi ve altındaki ${childCount} etiketin tamamı silinecek. Emin misiniz?`
      : `"${label}" kategorisini silmek istediğinize emin misiniz?`;
    if (!window.confirm(warn)) return;
    try {
      await axiosInstance.delete(`/bill-types/parents/${parentId}`);
      toast.success('Kategori silindi');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  const handleAdd = async (parent_key) => {
    const label = newLabel.trim();
    if (!label) return toast.error('Etiket boş olamaz');
    try {
      await axiosInstance.post('/bill-types', { parent_key, custom_label: label });
      toast.success('Etiket eklendi');
      setAddingFor(null);
      setNewLabel('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eklenemedi');
    }
  };

  const handleEdit = async (id) => {
    const label = editLabel.trim();
    if (!label) return toast.error('Etiket boş olamaz');
    try {
      await axiosInstance.put(`/bill-types/${id}`, { custom_label: label });
      toast.success('Güncellendi');
      setEditingId(null);
      setEditLabel('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu etiketi silmek istediğinize emin misiniz?')) return;
    try {
      await axiosInstance.delete(`/bill-types/${id}`);
      toast.success('Silindi');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Yükleniyor...</div>;
  }

  // System parents first (fixed order), then user-created parents (already
  // alphabetised by backend, appended in insertion order from `items`).
  const systemKeys = ['electric','water','gas','internet','mobile_phone','fixed_phone','building_fee','hvac'];
  const userKeys = Array.from(new Set(items
    .filter(it => it.custom_label === null && !it.is_system)
    .map(it => it.parent_key)));
  const orderedKeys = [...systemKeys, ...userKeys];

  // Max user-created parents per company — value comes from backend limits.
  const MAX_USER_PARENTS = limits.max_user_parents;
  const userParentCount = userKeys.length;
  const limitReached = userParentCount >= MAX_USER_PARENTS;

  return (
    <div className="space-y-4" data-testid="bill-types-tab">
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-sm text-gray-500 flex-1">
          8 sistem fatura kategorisi tüm Solo hesaplarda otomatik gelir. Her birinin altına
          kendi işletme etiketlerinizi (örn. <em>Mobil Telefon → Turkcell</em>) ekleyebilirsiniz.
          İsterseniz <strong>kendi kategorilerinizi de</strong> oluşturabilirsiniz (örn. Sigorta, Vergi).
        </p>
        {!creatingParent && (
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => { setCreatingParent(true); setNewParentLabel(''); }}
              disabled={limitReached}
              className={`px-3 py-2 rounded-md text-white text-xs font-medium flex items-center gap-1.5 ${
                limitReached ? 'bg-gray-300 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'
              }`}
              data-testid="bill-new-parent-btn"
              title={limitReached ? `En fazla ${MAX_USER_PARENTS} özel kategori eklenebilir` : 'Yeni özel kategori oluştur'}
            >
              <FolderPlusIcon className="w-4 h-4" />
              Yeni Kategori
            </button>
            <span
              className={`text-[11px] ${limitReached ? 'text-red-500 font-medium' : 'text-gray-400'}`}
              data-testid="bill-parent-counter"
            >
              {userParentCount}/{MAX_USER_PARENTS} özel kategori
            </span>
          </div>
        )}
      </div>

      {/* Kart / Liste görünüm modu geçiş — Genel Giderler ve Projeler
          sekmeleriyle aynı davranışta. Grid → 2-sütun kart; Liste → tek
          sütun tam-genişlik (mevcut varsayılan). */}
      <div className="flex items-center justify-start">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="bill-types-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
              viewMode === 'grid' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="bill-types-view-grid"
          >
            <LayoutGridIcon className="w-4 h-4" /> Kart
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
              viewMode === 'list' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="bill-types-view-list"
          >
            <ListIcon className="w-4 h-4" /> Liste
          </button>
        </div>
      </div>

      {creatingParent && (
        <div className="p-3 border-2 border-dashed border-teal-300 bg-teal-50 rounded-lg flex items-center gap-2">
          <FolderPlusIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
          <input
            type="text"
            value={newParentLabel}
            onChange={(e) => setNewParentLabel(e.target.value)}
            placeholder="Yeni kategori adı (örn. Sigorta, Vergi, Yıllık Aboneilkler...)"
            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-teal-500"
            data-testid="bill-new-parent-input"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateParent(); }}
          />
          <button onClick={handleCreateParent} className="p-1.5 text-green-600 hover:bg-green-100 rounded" data-testid="bill-new-parent-save">
            <CheckIcon className="w-4 h-4" />
          </button>
          <button onClick={() => { setCreatingParent(false); setNewParentLabel(''); }} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      <div
        className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 gap-4 items-start'
            : 'space-y-4'
        }
        data-testid={viewMode === 'grid' ? 'bill-types-grid' : 'bill-types-list'}
      >
      {orderedKeys.map((key) => {
        const group = groups[key];
        if (!group?.system) return null;
        const isUserCreated = !group.system.is_system;
        const Icon = isUserCreated ? TagIcon : (PARENT_ICONS[key] || BoltIcon);
        const isAdding = addingFor === key;
        // Mirror backend 50-label-per-parent cap
        const MAX_LABELS_PER_PARENT = 50;
        const labelLimitReached = group.customs.length >= MAX_LABELS_PER_PARENT;
        return (
          <div key={key} className={`border rounded-lg overflow-hidden ${isUserCreated ? 'border-teal-200' : 'border-gray-200'}`} data-testid={`bill-parent-${key}`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isUserCreated ? 'bg-teal-50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${isUserCreated ? 'text-teal-600' : 'text-gray-600'}`} />
                <span className="font-medium text-gray-900">{group.system.parent_label_tr}</span>
                {isUserCreated ? (
                  <span className="text-[10px] uppercase tracking-wide bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Özel</span>
                ) : (
                  <LockIcon className="w-3 h-3 text-gray-400" title="Sistem tanımlı, silinemez" />
                )}
                <span
                  className={`text-xs ${labelLimitReached ? 'text-red-500 font-medium' : 'text-gray-400'}`}
                  data-testid={`bill-label-counter-${key}`}
                >
                  ({group.customs.length}/{MAX_LABELS_PER_PARENT} etiket)
                </span>
              </div>
              <div className="flex items-center gap-1">
                {!isAdding && (
                  <button
                    type="button"
                    onClick={() => { if (!labelLimitReached) { setAddingFor(key); setNewLabel(''); } }}
                    disabled={labelLimitReached}
                    className={`text-xs flex items-center gap-1 ${
                      labelLimitReached
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-teal-600 hover:text-teal-700'
                    }`}
                    data-testid={`bill-add-btn-${key}`}
              title={labelLimitReached ? `Bir kategoriye en fazla ${MAX_LABELS_PER_PARENT} etiket eklenebilir` : 'Yeni etiket ekle'}
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Etiket Ekle
                  </button>
                )}
                {isUserCreated && (
                  <button
                    type="button"
                    onClick={() => handleDeleteParent(group.system.id, group.system.parent_label_tr, group.customs.length)}
                    className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Bu özel kategoriyi sil"
                    data-testid={`bill-parent-delete-${key}`}
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {isAdding && (
              <div className="px-4 py-3 bg-teal-50 border-b flex items-center gap-2">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder={`Örn: ${key === 'mobile_phone' ? 'Turkcell' : key === 'electric' ? 'CK Boğaziçi' : 'Etiket adı'}`}
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-teal-500"
                  data-testid={`bill-add-input-${key}`}
                  autoFocus
                />
                <button onClick={() => handleAdd(key)} className="p-1.5 text-green-600 hover:bg-green-100 rounded" data-testid={`bill-add-save-${key}`}>
                  <CheckIcon className="w-4 h-4" />
                </button>
                <button onClick={() => { setAddingFor(null); setNewLabel(''); }} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded">
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {group.customs.length === 0 ? (
              <div className="px-4 py-4 text-xs text-gray-400 italic">
                Henüz özel etiket eklenmedi.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {group.customs.map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50" data-testid={`bill-custom-${c.id}`}>
                    {editingId === c.id ? (
                      <>
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="flex-1 mr-2 px-2 py-1 text-sm border border-teal-300 rounded focus:outline-none focus:border-teal-500"
                          autoFocus
                        />
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleEdit(c.id)} className="p-1.5 text-green-600 hover:bg-green-100 rounded">
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setEditingId(null); setEditLabel(''); }} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded">
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-gray-800">
                          <span className="text-gray-400">{group.system.parent_label_tr} /</span> {c.custom_label}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setEditingId(c.id); setEditLabel(c.custom_label); }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            data-testid={`bill-edit-${c.id}`}
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            data-testid={`bill-delete-${c.id}`}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default BillTypesTab;
